import {
  type Block,
  CoreBatchAction,
  Factory,
  type ID,
  type NodeItem,
  ProjectActions,
  type SectionItem,
} from '@octopus-do/octopus-project-core'
import { buildCrawledBlock, resolveBlockColorId } from '@store/crawlImport'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { artBoardActions } from '@store/reducers/artboard'
import { getId } from '@store/utils'
import { commitProjectRequest } from '@storeOld/actionCreators'
import { closeModal } from '@storeOld/reducers/ui'
import { useDispatch, useStore } from 'react-redux'

import { BASE_COLOR_CLOUDY } from '@/constants/constants'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import type { AppState } from '@/types'
import { nearestHexColor } from '@/utils/colors'
import { nextSectionPlacement } from '@/utils/sectionPlacement'
import type { ImportModel, ImportPayload } from '../model'
import {
  buildImportModel,
  MIN_PAGES_TO_EXPAND,
  sectionTitleFromPayload,
} from '../model'

// Appending to a parent that already holds children. Adding a tree parent-first means
// every parent is empty when its children arrive, so this only ever means "in order".
const APPEND = -1

/**
 * Lands a crawl in the project that is already open, as one new section in the active tab.
 *
 * Everything goes out as a single batched commit, for two reasons. Raw project actions
 * dispatched one by one never reach the sync server at all — the commit middleware only
 * forwards COMMIT_PROJECT_REQUEST — so a page-by-page import would live in local state
 * and vanish on reload. And one commit is one history entry, so one undo takes the whole
 * import back out instead of several hundred.
 */
export const useCommitIntoProject = () => {
  const dispatch = useDispatch()
  const store = useStore()

  /**
   * The crawl asks for its blocks in fixed hexes, which a project whose palette has been
   * edited simply does not have. Falling back to the project default would paint title,
   * description and H1 the same colour and lose the distinction the colours exist for, so
   * an absent colour resolves to the nearest one the project does have.
   *
   * Nothing is added to the palette: an import must not change the project's colours or
   * its legend.
   */
  const resolveColorId = (
    state: AppState,
    hex: string | null,
    defaultColorId: ID
  ): ID => {
    if (!hex) {
      return defaultColorId
    }

    const exact = migrationProjectSelector.getProjectColorByHex(hex)(state)

    if (exact) {
      return exact.id
    }

    const palette = state.manager.project.colors
    const nearest = nearestHexColor(
      hex,
      palette.map((color) => color.hexCode)
    )

    return (
      palette.find((color) => color.hexCode === nearest)?.id ?? defaultColorId
    )
  }

  const buildNodeItems = (model: ImportModel, defaultColorId: ID): NodeItem[] =>
    model.nodes.map((node) => ({
      id: node.id,
      title: node.name,
      // A crawl root is judged by the model's own notion of a root, not by whether it
      // ended up with a parent here. Landing roots under a section would otherwise flip
      // this rule and collapse the top level, which it has never done.
      collapsed:
        node.parentId !== null && node.childrenNumber >= MIN_PAGES_TO_EXPAND,
      variant: 'default' as const,
      colorId: defaultColorId,
      notes: {
        ...(node.seoTitle ? { seoTitle: node.seoTitle } : {}),
        ...(node.seoDescription ? { seoDescription: node.seoDescription } : {}),
        ...(node.seoH1 ? { seoH1: node.seoH1 } : {}),
        ...(node.seoSlug ? { seoSlug: node.seoSlug } : {}),
      },
      // Deliberately no notes.seoUrl: since OCT-4991 a page has one address and it is
      // node.url. Writing the legacy field would mint new data the migration is retiring.
      url: node.url || null,
      image: null,
      figma: null,
    }))

  /**
   * One `nodes.add` carries a single parent for every item in it, so a tree needs one add
   * per parent. Grouping walks the model in order, which is parent-before-child, and that
   * makes the resulting group order safe: a parent is always registered in relations before
   * the group holding its children is applied. Get that order wrong and the child keeps a
   * pointer to its parent while the parent never learns about the child — the page exists
   * and is invisible.
   */
  const groupByParent = (
    nodes: NodeItem[],
    model: ImportModel,
    rootParentId: ID
  ): Map<ID, NodeItem[]> => {
    const groups = new Map<ID, NodeItem[]>()

    model.nodes.forEach((modelNode, index) => {
      const parentId = modelNode.parentId ?? rootParentId
      const group = groups.get(parentId)

      if (group) {
        group.push(nodes[index])
        return
      }

      groups.set(parentId, [nodes[index]])
    })

    return groups
  }

  const commitIntoProject = async (payload: ImportPayload) => {
    const state = store.getState() as AppState
    const model = buildImportModel(payload, getId)

    if (!model.nodes.length) {
      dispatch(closeModal())
      return
    }

    const activeTab =
      migrationProjectSelector.getActiveOrFirstTabId(state) ||
      state.manager.project.tabs[0]?.id

    // Every refusal below throws rather than returning quietly: the caller clears its
    // pending state in .catch and shows the message, so a silent return would leave the
    // confirm button disabled and the modal open for good. These strings are read by a
    // user, not a log.
    if (!activeTab) {
      throw new Error(
        'There is no tab in this project to import the pages into.'
      )
    }

    // The same conditions the commit middleware checks before it posts anything. Checking
    // them here too is not belt and braces: the middleware applies the reducers first and
    // only then refuses, so without this the pages would appear locally, never reach the
    // server, and the modal would close as though the import had worked.
    const { canEdit, canEditContent } = state.manager.meta.access

    if (state.artboard?.isDisabled || (!canEdit && !canEditContent)) {
      throw new Error('You cannot add pages to this project right now.')
    }

    const defaultColorId =
      migrationProjectSelector.getProjectDefaultColorId(state) ||
      state.manager.project.colors[0]?.id ||
      BASE_COLOR_CLOUDY.id

    const { index: sectionIndex, order: sectionOrder } = nextSectionPlacement(
      state,
      activeTab
    )
    const sectionId = getId()

    const section: SectionItem = {
      id: sectionId,
      title: sectionTitleFromPayload(payload),
      tabId: activeTab,
      colorId: BASE_COLOR_CLOUDY.id,
      order: sectionOrder,
      collapsed: false,
      textSize: 's',
    }

    const nodes = buildNodeItems(model, defaultColorId)
    const nodesByParent = groupByParent(nodes, model, sectionId)

    const blocksByNode = new Map<ID, Block[]>()

    const pushBlock = (nodeId: ID, item: Block): void => {
      const group = blocksByNode.get(nodeId)

      if (group) {
        group.push(item)
        return
      }

      blocksByNode.set(nodeId, [item])
    }

    model.blocks.forEach((block) => {
      pushBlock(
        block.nodeId,
        Factory.block.monolithic({
          id: block.id,
          colorId: resolveColorId(state, block.colorHex, defaultColorId),
          title: block.name,
          visual: Factory.block.wireframesVisual({ wireframes: [] }),
        })
      )
    })

    // The blocks the crawler inferred from the markup (OCT-4990). The new-project path
    // hands these to crawlImportSaga, which puts them in as raw project actions — no use
    // here, since raw actions never reach the sync server. Same blocks, same helpers,
    // laid into the batch instead.
    model.crawledPages.forEach((page) => {
      page.blocks.forEach((block) => {
        pushBlock(
          page.nodeId,
          buildCrawledBlock({
            block,
            colorId: resolveBlockColorId(state, block.color),
          })
        )
      })
    })

    dispatch(
      commitProjectRequest(
        CoreBatchAction({
          type: 'section_creation',
          actions: [
            ProjectActions.sections.add({
              items: [section],
              index: sectionIndex,
              parents: [activeTab],
            }),
            ...Array.from(nodesByParent, ([parentId, items]) =>
              ProjectActions.nodes.add({
                items,
                index: APPEND,
                parents: [parentId],
              })
            ),
            ...Array.from(blocksByNode, ([nodeId, items]) =>
              ProjectActions.blocks.add({
                items,
                index: APPEND,
                parents: [nodeId],
              })
            ),
          ],
        })
      )
    )

    // The section lands at the end of the tab, which on any project with something in it
    // is off screen: without this the import reads as having done nothing.
    dispatch(artBoardActions.revealSection({ id: sectionId }))

    // The crawl-conversion event has always been emitted from saveProjectSaga, keyed on
    // ProjectOrigin. This path never calls that saga, so without its own event the import
    // would be invisible in GA — and the existing counter would appear to drop.
    sendGtmEvent(
      GtmTriggerName.CRAWL_IMPORT_INTO_PROJECT,
      'Crawl imported into an existing project'
    )

    dispatch(closeModal())
  }

  return commitIntoProject
}
